const id = (value) => encodeURIComponent(String(value))

export const paths = {
  products: {
    list: '/products',
    new: '/products/new',
    edit: (productId) => `/products/${id(productId)}/edit`,
  },
  labors: {
    list: '/labors',
    new: '/labors/new',
    edit: (laborId) => `/labors/${id(laborId)}/edit`,
  },
  packages: {
    list: '/packages',
    new: '/packages/new',
    edit: (packageId) => `/packages/${id(packageId)}/edit`,
  },
  categories: { list: '/categories' },
  brands: { list: '/brands' },
  productTags: { list: '/product-tags' },
  productBadges: { list: '/product-badges' },
  attributeDefinitions: { list: '/attribute-definitions' },
  locations: { list: '/locations' },
  uoms: { list: '/uoms' },
  payTerms: { list: '/pay-terms' },
  workOrderTypes: { list: '/work-order-types' },
  taxes: { list: '/taxes' },
  paymentAccounts: { list: '/payment-accounts' },
  paymentTypes: { list: '/payment-types' },
  deviceTypes: { list: '/device-types' },
  expenseCategories: {
    list: '/expense-categories',
    edit: (categoryId) => `/expense-categories/${id(categoryId)}/edit`,
  },
  vendors: {
    list: '/vendors',
    new: '/vendors/new',
    edit: (vendorId) => `/vendors/${id(vendorId)}/edit`,
  },
  customers: {
    list: '/customers',
    new: '/customers/new',
    edit: (customerId) => `/customers/${id(customerId)}/edit`,
  },
  warehouses: {
    list: '/warehouses',
    new: '/warehouses/new',
    edit: (warehouseId) => `/warehouses/${id(warehouseId)}/edit`,
  },
  expenses: {
    list: '/expenses',
    new: '/expenses/new',
    edit: (expenseId) => `/expenses/${id(expenseId)}/edit`,
  },
  adjustments: {
    list: '/adjustments',
    new: '/adjustments/new',
    edit: (adjustmentId) => `/adjustments/${id(adjustmentId)}/edit`,
  },
  invoices: {
    list: '/invoices',
    new: '/invoices/new',
    detail: (invoiceId) => `/invoices/${id(invoiceId)}`,
    edit: (invoiceId) => `/invoices/${id(invoiceId)}/edit`,
  },
  estimates: {
    new: '/estimates/new',
    edit: (estimateId) => `/estimates/${id(estimateId)}/edit`,
  },
  workOrders: {
    list: '/work-orders',
    new: '/work-orders/new',
    edit: (workOrderId) => `/work-orders/${id(workOrderId)}/edit`,
  },
  payments: {
    new: '/payments/new',
    edit: (paymentId) => `/payments/${id(paymentId)}/edit`,
  },
  receiptPayments: {
    new: '/receipt-payments/new',
    createForReceipt: (receiptId) => `/receipt-payments/new/${id(receiptId)}`,
    edit: (paymentId) => `/receipt-payments/${id(paymentId)}/edit`,
  },
  receipts: { list: '/receipts' },
  onlineSales: {
    list: '/online-sales',
    withSale: (saleId) => `/online-sales?saleId=${id(saleId)}`,
  },
  saleReturns: {
    list: '/sale-returns',
    detail: (returnId) => `/sale-returns/${id(returnId)}`,
  },
  storefrontInquiries: {
    list: '/storefront-inquiries',
    detail: (inquiryId) => `/storefront-inquiries/${id(inquiryId)}`,
  },
  storefrontLegal: { list: '/settings/legal-policies' },
  receivePayments: { list: '/receive-payments' },
  workOrderPayments: { list: '/work-orders-payments' },
  companies: {
    list: '/admin/companies',
    deleted: '/companies/deleted',
    new: '/companies/new',
    edit: (companyId) => `/companies/${id(companyId)}/edit`,
  },
  users: {
    list: '/users',
    new: '/users/new',
    edit: (userId) => `/users/${id(userId)}/edit`,
    accessProfiles: '/users/access-profiles',
  },
  devices: {
    list: '/devices',
    new: '/devices/new',
    edit: (deviceId) => `/devices/${id(deviceId)}/edit`,
  },
  itemInventory: { list: '/item-inventory' },
  stockReceipts: {
    list: '/stock-receipts',
    new: '/stock-receipts/new',
    detail: (receiptId) => `/stock-receipts/${id(receiptId)}`,
  },
  stockTransfers: {
    list: '/stock-transfers',
    new: '/stock-transfers/new',
    detail: (transferId) => `/stock-transfers/${id(transferId)}`,
  },
  purchaseOrders: {
    list: '/purchase-orders',
    new: '/purchase-orders/new',
    detail: (orderId) => `/purchase-orders/${id(orderId)}`,
  },
  vendorBills: {
    list: '/vendor-bills',
    new: '/vendor-bills/new',
    detail: (billId) => `/vendor-bills/${id(billId)}`,
  },
  recurringInvoices: {
    list: '/recurring-invoices',
    new: '/recurring-invoices/new',
    detail: (recurringId) => `/recurring-invoices/${id(recurringId)}`,
    edit: (recurringId) => `/recurring-invoices/${id(recurringId)}/edit`,
  },
  dashboard: '/dashboard',
  accessDenied: '/access-denied',
  pos: '/pos',
  profile: '/profile',
  accounting: {
    overview: '/accounting/overview',
    profitLoss: '/accounting/profit-loss',
    accountsReceivable: '/accounting/accounts-receivable',
    salesReport: '/accounting/sales-report',
    paymentsReport: '/accounting/payments-report',
    expensesReport: '/accounting/expenses-report',
  },
  admin: {
    companies: '/admin/companies',
    companyHealth: (companyId) => `/admin/companies/${id(companyId)}/health`,
    accountDeletions: '/admin/account-deletions',
    salesLeads: {
      list: '/admin/sales-leads',
      detail: (leadId) => `/admin/sales-leads/${id(leadId)}`,
    },
    superAdmins: '/admin/super-admins',
    superAdminNew: '/admin/super-admins/new',
    superAdminEdit: (userId) => `/admin/super-admins/${id(userId)}/edit`,
    activity: '/admin/activity',
    platformHealth: '/admin/platform-health',
    socialContentPrompt: '/admin/social-content-prompt',
    creativeVisionPrompt: '/admin/creative-vision-prompt',
    pushNotifications: '/admin/push-notifications',
    pushNotificationsSend: '/admin/push-notifications/send',
    pushNotificationsHistory: '/admin/push-notifications/history',
  },
  notifications: '/notifications',
  barcodeGenerator: '/barcode-generator',
  inventoryReport: '/inventory-report',
  settings: {
    chartOfAccounts: '/settings/chart-of-accounts',
    payment: '/settings/payment',
    sms: '/settings/sms',
    pushNotifications: '/settings/push-notifications',
    subscription: '/settings/subscription',
    account: '/settings/account',
    legalPolicies: '/settings/legal-policies',
    storefrontProfile: '/settings/storefront-profile',
    ecommerceShipping: '/settings/ecommerce-shipping',
    storefrontReviews: '/settings/storefront-reviews',
    productReviews: '/settings/product-reviews',
    productImageOptimization: '/settings/product-image-optimization',
    socialContent: '/settings/social-content',
  },
  marketing: {
    socialContent: '/marketing/social-content',
    creativeStudio: '/marketing/creative-studio',
  },
  account: {
    recover: '/account/recover',
    cancelDeletion: '/account/deletion/cancel',
  },
  billing: {
    success: '/billing/success',
    cancel: '/billing/cancel',
    portalReturn: '/billing',
    paypalReturn: '/settings/billing/paypal/return',
    paypalCancel: '/settings/billing/paypal/cancel',
  },
}

/** React Router patterns for nested *Routes Switch — use with useRouteId({ pathPatterns }). */
export const routePathPatterns = {
  productsEdit: '/products/:id/edit',
  laborsEdit: '/labors/:id/edit',
  packagesEdit: '/packages/:id/edit',
  expenseCategoriesEdit: '/expense-categories/:id/edit',
  vendorsEdit: '/vendors/:id/edit',
  customersEdit: '/customers/:id/edit',
  warehousesEdit: '/warehouses/:id/edit',
  expensesEdit: '/expenses/:id/edit',
  adjustmentsEdit: '/adjustments/:id/edit',
  invoicesEdit: '/invoices/:id/edit',
  invoicesDetail: '/invoices/:id',
  estimatesEdit: '/estimates/:id/edit',
  workOrdersEdit: '/work-orders/:id/edit',
  paymentsEdit: '/payments/:id/edit',
  receiptPaymentsCreate: '/receipt-payments/new/:receiptId',
  receiptPaymentsEdit: '/receipt-payments/:id/edit',
  companiesEdit: '/companies/:id/edit',
  usersEdit: '/users/:id/edit',
  devicesEdit: ['/devices/:id/edit', '/devices/:deviceId/edit'],
  recurringInvoicesEdit: '/recurring-invoices/:id/edit',
  recurringInvoicesDetail: '/recurring-invoices/:id',
  stockReceiptsDetail: '/stock-receipts/:id',
  purchaseOrdersDetail: '/purchase-orders/:id',
  stockTransfersDetail: '/stock-transfers/:id',
  vendorBillsDetail: '/vendor-bills/:id',
  superAdminsEdit: '/admin/super-admins/:id/edit',
}

export const legacyPathRedirects = [
  { from: '/product_list', to: paths.products.list },
  { from: '/create_item_form', to: paths.products.new },
  { from: '/create_item', to: paths.products.new },
  { from: '/labors_list', to: paths.labors.list },
  { from: '/create_labor_form', to: paths.labors.new },
  { from: '/packages_list', to: paths.packages.list },
  { from: '/create_package_form', to: paths.packages.new },
  { from: '/categories_list', to: paths.categories.list },
  { from: '/brand_list', to: paths.brands.list },
  { from: '/location_list', to: paths.locations.list },
  { from: '/uom_list', to: paths.uoms.list },
  { from: '/payment_account_list', to: paths.paymentAccounts.list },
  { from: '/type_payment_list', to: paths.paymentTypes.list },
  { from: '/type_device_list', to: paths.deviceTypes.list },
  { from: '/offline_payment_methods_list', to: paths.admin.companies },
  { from: '/payment-methods', to: paths.admin.companies },
  { from: '/expensive_categories', to: paths.expenseCategories.list },
  { from: '/vendors_list', to: paths.vendors.list },
  { from: '/create_vendor', to: paths.vendors.new },
  { from: '/costumer_list', to: paths.customers.list },
  { from: '/costumer_form', to: paths.customers.new },
  { from: '/warehouse', to: paths.warehouses.list },
  { from: '/create/warehouse', to: paths.warehouses.new },
  { from: '/expensive', to: paths.expenses.list },
  { from: '/create_expensive', to: paths.expenses.new },
  { from: '/adjustment', to: paths.adjustments.list },
  { from: '/adjustment_form', to: paths.adjustments.new },
  { from: '/invoice', to: paths.invoices.list },
  { from: '/invoice_form', to: paths.invoices.new },
  { from: '/estimate_form', to: paths.estimates.new },
  { from: '/work-order', to: paths.workOrders.list },
  { from: '/work_order_form', to: paths.workOrders.new },
  { from: '/payment_form', to: paths.payments.new },
  { from: '/receipt_payment_form', to: paths.receiptPayments.new },
  { from: '/receipt', to: paths.receipts.list },
  { from: '/sales-online', to: paths.onlineSales.list },
  { from: '/company_list', to: paths.companies.list },
  { from: '/company_list_deleted', to: paths.companies.deleted },
  { from: '/create_company_form', to: paths.companies.new },
  { from: '/user_managment', to: paths.users.list },
  { from: '/create_userManagment', to: paths.users.new },
  { from: '/item_location', to: paths.itemInventory.list },
  { from: '/devices/create', to: paths.devices.new },
]

export const legacyQueryRedirectRules = [
  { pathname: '/update_item_form', queryKey: 'product_id', buildTarget: paths.products.edit },
  { pathname: '/update_item', queryKey: 'product_id', buildTarget: paths.products.edit },
  { pathname: '/update_labor_form', queryKey: 'labor_id', buildTarget: paths.labors.edit },
  { pathname: '/update_package_form', queryKey: 'package_id', buildTarget: paths.packages.edit },
  { pathname: '/costumer_form_update', queryKey: 'costumer_id', buildTarget: paths.customers.edit },
  { pathname: '/update_company_form', queryKey: 'company_id', buildTarget: paths.companies.edit },
  { pathname: '/invoice_form_update', queryKey: 'invoice_id', buildTarget: paths.invoices.edit },
  { pathname: '/estimate_form_update', queryKey: 'invoice_id', buildTarget: paths.estimates.edit },
  { pathname: '/work_order_form_update', queryKey: 'invoice_id', buildTarget: paths.workOrders.edit },
  { pathname: '/adjustment_form_update', queryKey: 'adjustment_id', buildTarget: paths.adjustments.edit },
  { pathname: '/expensive_detail', queryKey: 'expensive_id', buildTarget: paths.expenses.edit },
  { pathname: '/payments/new', queryKey: 'payment_id', buildTarget: paths.payments.edit },
  { pathname: '/payment_form', queryKey: 'payment_id', buildTarget: paths.payments.edit },
  { pathname: '/receipt-payments/new', queryKey: 'receipt_id', buildTarget: paths.receiptPayments.createForReceipt },
  { pathname: '/receipt_payment_form', queryKey: 'receipt_id', buildTarget: paths.receiptPayments.createForReceipt },
  { pathname: '/receipt-payments/new', queryKey: 'payment_id', buildTarget: paths.receiptPayments.edit },
  { pathname: '/receipt_payment_form', queryKey: 'payment_id', buildTarget: paths.receiptPayments.edit },
]

export default paths
